import { useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useGetTransactionsByContractId } from "@rentablo/api/contracts";

interface Params {
  contractId: number;
  userId: number | undefined;
  isLoadingUser: boolean;
  splitSelection: string[];
}

export const useContractTransactionsData = ({
  contractId,
  userId,
  isLoadingUser,
  splitSelection,
}: Params) => {
  const { data: contractTransactions, isLoading } =
    useGetTransactionsByContractId(
      {
        contractId,
        userId: userId!,
      },
      {
        enabled: Boolean(!isLoadingUser && userId),
        placeholderData: keepPreviousData,
      }
    );

  const transactions = useMemo(
    () =>
      [...(contractTransactions?.transactions ?? [])].sort(
        (a, b) =>
          new Date(b.bookingDate ?? 0).getTime() -
          new Date(a.bookingDate ?? 0).getTime()
      ),
    [contractTransactions]
  );

  const totalTransactions = transactions.length;

  const isInternalTransfer = useMemo(
    () =>
      Boolean(
        transactions.length && transactions.every((tx) => tx.purpose === "ID")
      ),
    [transactions]
  );

  const splitSelectionTransactions = useMemo(
    () => transactions.filter((tx) => splitSelection.includes(tx.id)),
    [transactions, splitSelection]
  );

  const transactionIds = useMemo(
    () => transactions.map((tx) => Number(tx.id)),
    [transactions]
  );

  return {
    contractTransactions,
    transactions,
    totalTransactions,
    isInternalTransfer,
    splitSelectionTransactions,
    transactionIds,
    isLoading,
  };
};
